/**
 * A person, as a face when they have uploaded one and initials when they have not.
 *
 * The photo is fetched through `api.blob` rather than pointed at with a plain
 * `<img src>`: the avatar endpoints check who is asking, and a bare URL in the
 * markup would arrive without the session.
 *
 * The blob is cached by react-query under the storage key (`version`), which the
 * API changes on every upload — so a replaced photo appears everywhere as soon as
 * the session payload refreshes, with no cache-busting ceremony. Callers that do
 * not know whether a photo exists (a workflow timeline knows only the actor's id)
 * simply pass the endpoint; a 404 lands as the initials fallback, silently.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "@/lib/api";
import { cn, initials } from "@/lib/format";

export function Avatar({
  name,
  src,
  version,
  className,
}: {
  name: string | null | undefined;
  /** Authenticated endpoint for the image, or null/undefined when nothing is on record. */
  src?: string | null;
  /** The avatar's storage key when known. A new upload changes it, which is the refetch. */
  version?: string | null;
  /** Size and text size, e.g. "size-8 text-xs". */
  className?: string;
}) {
  const { data: blob } = useQuery({
    queryKey: ["avatar", src, version ?? "unversioned"],
    queryFn: () => api.blob(src!),
    enabled: Boolean(src),
    staleTime: 5 * 60_000,
    // A 404 means "no photo", which is an answer, not a failure to retry.
    retry: false,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full bg-brand-tint font-semibold text-brand",
        className,
      )}
    >
      {url ? (
        <img src={url} alt={name ?? ""} className="size-full object-cover" />
      ) : (
        initials(name)
      )}
    </span>
  );
}
